import { EFileCollection } from '@/modules/productModule/constants/productConstants'
import type { IFile } from '@/types/globalTypes'

const groupPrefix = 'news'

const buildNewsListQuery = (page = 1, perPage = 10, search?: string, language?: string, sortBy?: string, orderBy?: string) => {
	const queryParams = []

	if (perPage && page) {
		queryParams.push(`per_page=${perPage}`)
		queryParams.push(`page=${page}`)
	}

	if (search) {
		queryParams.push(`search=${encodeURIComponent(search)}`)
	}

	if (language) {
		queryParams.push(`language=${encodeURIComponent(language)}`)
	}

	if (sortBy) {
		queryParams.push(`sort_by=${encodeURIComponent(sortBy)}`)
	}

	if (orderBy) {
		queryParams.push(`order_by=${encodeURIComponent(orderBy)}`)
	}

	return queryParams.length > 0 ? `?${queryParams.join('&')}` : ''
}

const fetchNewsList = async (
	baseUrl: string,
	page = 1,
	perPage = 10,
	search?: string,
	language?: string,
	sortBy = 'published_at',
	orderBy = 'desc',
) => {
	const url = `${baseUrl}${buildNewsListQuery(page, perPage, search, language, sortBy, orderBy)}`
	const { data, links, meta } = await apiCommon<INews[]>(url, { method: 'GET' })

	if (!data) return { response: [], links, meta }

	const response = data.map((news) => News(news))
	return { data: response, links, meta }
}

export const apiNews = {
	fetchNewsBySlug: async (slug: string, lang: string | null = null) => {
		let url = `${groupPrefix}/by-slug/${slug}`
		if (lang) url += `?lang=${encodeURIComponent(lang)}`
		const { data } = await apiCommon<INews>(url, { method: 'GET' })
		console.log('data', data)
		return data ? News(data) : null
	},
	fetchCategoryBySlug: async (slug: string, lang: string | null = null) => {
		let url = `news-categories/by-slug/${slug}`
		if (lang) url += `?lang=${encodeURIComponent(lang)}`
		const { data } = await apiCommon<INewsCategory>(url, { method: 'GET' })
		return data ? NewsCategory(data) : null
	},
	fetchNewsByCategorySlug: async (slug: string, page = 1, perPage = 10, search?: string, language?: string, sortBy?: string, orderBy?: string) => {
		return await fetchNewsList(`news-categories/by-slug/${slug}/news`, page, perPage, search, language, sortBy, orderBy)
	},

	fetchNewsCategories: async (lang?: string) => {
		const { data } = await apiCommon<INewsCategory[]>('news-categories', {
			method: 'GET',
			query: { lang: lang?.toLowerCase() },
		})
		if (!data) return []
		return data.map((category) => NewsCategory(category))
	},

	createNewsCategory: async (categoryData: INewsCategoryForm) => {
		const { data } = await apiCommon<INewsCategory>('news-categories', { method: 'POST', body: categoryData })
		return NewsCategory(data)
	},

	updateNewsCategory: async (newsCategoryID: number | string, categoryData: INewsCategoryForm) => {
		const { data } = await apiCommon<INewsCategory>(`news-categories/${newsCategoryID}`, { method: 'PUT', body: categoryData })
		return NewsCategory(data)
	},

	deleteNewsCategory: async (newsCategoryID: number | string) => {
		return await apiCommon<string>(`news-categories/${newsCategoryID}`, { method: 'DELETE' })
	},

	fetchSingleNews: async (newsID: number, lang = null) => {
		let url = `${groupPrefix}/${newsID}`

		const queryParams = []

		if (lang) {
			queryParams.push(`lang=${encodeURIComponent(lang)}`)
		}

		if (queryParams.length > 0) {
			url += `?${queryParams.join('&')}`
		}

		const { data } = await apiCommon<INews>(url, { method: 'GET' })
		return News(data)
	},

	fetchNews: async (page = 1, perPage = 10, search?: string, language?: string, sortBy?: string, orderBy?: string) => {
		return await fetchNewsList(groupPrefix, page, perPage, search, language, sortBy, orderBy)
	},

	updateNews: async (newsData: Partial<INewsModel>, newsID: number) => {
		const { data } = await apiCommon<INews>(`${groupPrefix}/${newsID}`, { method: 'PUT', body: newsToJSON(newsData) })
		return News(data)
	},

	createNews: async (newsData: Partial<INewsModel>) => {
		const { data } = await apiCommon<INews>(`${groupPrefix}`, {
			method: 'POST',
			body: newsToJSON(newsData),
		})
		return News(data)
	},

	deleteNews: async (newsID: number) => {
		const data = await apiCommon<string>(`${groupPrefix}/${newsID}`, { method: 'DELETE' })
		return data
	},

	// for files
	uploadNewsImage: async (newsID: number, file: File, collection: EFileCollection) => {
		const formData = new FormData()
		formData.append('file', file, file.name)
		formData.append('collection', collection)

		const { data } = await apiCommon<{ data: IFile }>(`files/news/${newsID}`, {
			method: 'POST',
			body: formData,
		})
		return FileFromJSON(data)
	},

	deleteNewsImage: async (fileID: string) => {
		const data = await apiCommon<string>(`files/${fileID}`, {
			method: 'DELETE',
		})
		return data
	},
}
